import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import type { Path } from 'react-hook-form';

import { GENERAL_INFORMATION_SECTION_ID, ROUTE_INFORMATION_SECTION_ID, SCHEDULING_SECTION_ID } from '../constants';
import type { OrderCreationSectionId } from '../constants';
import type { OrderCreationFormInput } from '../schemas/order-creation.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderCreationErrorDetail = Pick<ApiErrorDetail, 'errorCode'>;

export interface OrderCreationErrorMapping {
  /** RHF field to attach a manual error to, when the rejection targets one specific field. */
  field?: Path<OrderCreationFormInput>;
  /** i18n key used for both the field error (if any) and the toast description. */
  messageKey: string;
  /** Section to reopen via `uncompleteSection`, when the rejection targets a whole section. */
  section?: OrderCreationSectionId;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ERROR_MAPPING: OrderCreationErrorMapping = { messageKey: 'order-creation:submit.errorDescription' };

// Namespace-prefixed so the mapper works regardless of the caller's default i18next namespace,
// mirroring orders/utils/order-update-error.ts.
const ORDER_CREATION_ERROR_MAP: Record<string, OrderCreationErrorMapping> = {
  'BadRequest.DeliveryAddressMissingFullOrCity': {
    messageKey: 'order-creation:submit.errors.deliveryAddressMissingFullOrCity',
    section: ROUTE_INFORMATION_SECTION_ID,
  },
  'BadRequest.DeliveryMissingLocation': {
    messageKey: 'order-creation:submit.errors.deliveryMissingLocation',
    section: ROUTE_INFORMATION_SECTION_ID,
  },
  'BadRequest.InvalidTimeWindow': {
    field: 'scheduling',
    messageKey: 'order-creation:submit.invalidTimeWindowError',
    section: SCHEDULING_SECTION_ID,
  },
  'BadRequest.NoActiveShipperConfiguration': { messageKey: 'order-creation:submit.shipper.configurationError' },
  'Conflict.DuplicateOrder': {
    field: 'generalInformation.referenceNumber',
    messageKey: 'order-creation:sections.generalInformation.fields.reference.duplicateError',
    section: GENERAL_INFORMATION_SECTION_ID,
  },
  'Forbidden.ScopeViolation': { messageKey: 'order-creation:submit.errors.scopeViolation' },
};

// ─── Utils ────────────────────────────────────────────────────────────────────

/**
 * Maps a `POST /orders` rejection to its field/section/message handling. Unmapped codes fall back
 * to a generic translated message — never the raw API message, which may be in English regardless
 * of the user's locale.
 */
export function resolveOrderCreationErrorMapping(detail: OrderCreationErrorDetail): OrderCreationErrorMapping {
  if (!detail.errorCode) return DEFAULT_ERROR_MAPPING;
  return ORDER_CREATION_ERROR_MAP[detail.errorCode] ?? DEFAULT_ERROR_MAPPING;
}
